import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Anchor, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import WizardProgress from "@/components/wizard/WizardProgress";
import Step1Upload from "@/components/wizard/Step1Upload";
import Step2Calibration from "@/components/wizard/Step2Calibration";
import SequenceLab from "@/components/wizard/SequenceLab";
import AnchorCollectMode from "@/components/wizard/AnchorCollectMode";
import HelpButton from "@/components/HelpButton";
import { getSequence, autoCalibrate } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { sprungParameter } from "@/lib/klickliste";
import { ebenenAusSprung } from "@/lib/linienpruefung";
import BodenklickStrecke from "@/components/wizard/BodenklickStrecke";
import AnkerPruefliste from "@/components/wizard/AnkerPruefliste";
import { schalterAn } from "@/lib/bodenklicks";

const BODENKLICK_AN = schalterAn();

const STEP_LABELS = {
  1: "Upload & Game Configuration",
  2: "Field Calibration · 4-Point (optional)",
  3: "Sequence Lab · Cut · Track · Label",
};

const STEP_HELP_TOPIC = { 1: "step1", 2: "step2", 3: "step3" };

export default function Wizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sequenceId, setSequenceId] = useState(id || null);
  const [sequence, setSequence] = useState(null);
  const [showAnchorMode, setShowAnchorMode] = useState(false);
  const [showBodenklick, setShowBodenklick] = useState(false);
  // 🆕 Auftrag 29, R1/R3 · `?anker=1&t=…[&liste=…&nr=…]` öffnet den
  // Anker-Sammelmodus am Bild t. Ohne die Parameter bleibt alles, wie es ist.
  const [searchParams] = useSearchParams();
  const sprung = useMemo(() => sprungParameter(searchParams), [searchParams]);
  const ebenen = useMemo(() => ebenenAusSprung(sprung), [sprung]);
  useEffect(() => {
    if (sprung.anker) setShowAnchorMode(true);
  }, [sprung.anker]);

  useEffect(() => {
    if (!id) return;
    setSequenceId(id);
    (async () => {
      const doc = await getSequence(id);
      setSequence(doc);
      // 🆕 Broadcast-Workflow: Kalibrierung ist OPTIONAL — nach dem
      // Upload direkt ins Sequence Lab (Step 2 bleibt via Back erreichbar).
      if (doc.source_video_path) setStep(3);
      else setStep(1);
    })();
  }, [id]);

  const refreshSeq = async () => {
    if (sequenceId) {
      const doc = await getSequence(sequenceId);
      setSequence(doc);
      return doc;
    }
  };

  const onStep1Done = (seqId, seq) => {
    setSequenceId(seqId);
    setSequence(seq);
    // 🆕 Broadcast-Workflow: 4-Punkt-Kalibrierung ist keine Pflicht mehr —
    // direkt ins Sequence Lab + DL-Auto-Kalibrierung im Hintergrund.
    setStep(3);
    runAutoCalibrate(seqId);
  };

  const runAutoCalibrate = async (seqId) => {
    toast.info("DL-Auto-Kalibrierung läuft im Hintergrund…", { duration: 6000 });
    try {
      const r = await autoCalibrate(seqId);
      if (r?.solved) {
        toast.success(
          `Feld automatisch erkannt (${r.variant || "DL"} · ${Number(r.reproj_error_px || 0).toFixed(1)} px) — Auto-Anker gespeichert`,
          { duration: 9000 },
        );
        await refreshSeq();
      } else {
        toast.warning(
          "Auto-Kalibrierung nicht möglich — im Anker-Sammelmodus 2–3 sichtbare Linien frei nachziehen",
          { duration: 9000 },
        );
      }
    } catch {
      toast.warning(
        "Auto-Kalibrierung nicht möglich — im Anker-Sammelmodus 2–3 sichtbare Linien frei nachziehen",
        { duration: 9000 },
      );
    }
  };

  const onStep2Done = async () => { await refreshSeq(); setStep(3); };
  const onFinish = () => navigate("/");

  return (
    <div data-testid={TID.wizardRoot} className="min-h-screen flex flex-col">
      <header className="px-8 pt-8 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              New Match Wizard
            </div>
            <h1 className="font-display font-black uppercase text-3xl tracking-tight">
              {STEP_LABELS[step]}
            </h1>
            {sequence?.name && (
              <div className="font-mono text-xs text-muted-foreground mt-2">
                <span className="text-primary">▸</span> {sequence.name} · #{sequenceId?.slice(0, 8)}
              </div>
            )}
          </div>
          <HelpButton topic={STEP_HELP_TOPIC[step]} />
        </div>
      </header>

      <WizardProgress current={step} />

      {/* 🆕 v1 · Anker-Sammelmodus — Button ab Step 2 immer sichtbar;
          🆕 P3P: funktioniert auch OHNE persistierte Basis-Kalibrierung. */}
      {step >= 2 && (
        <div className="px-8 pt-4">
          <div className="flex items-center gap-3">
            <Button
              data-testid="anchor-collect-toggle"
              variant="outline"
              size="sm"
              onClick={() => setShowAnchorMode((v) => !v)}
              className={`font-mono text-[11px] uppercase tracking-widest ${
                showAnchorMode
                  ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                  : "border-border"
              }`}
            >
              <Anchor className="w-3.5 h-3.5 mr-2" />
              Anker-Sammelmodus {showAnchorMode ? "schließen" : "öffnen"}
            </Button>
            {/* 🆕 Auftrag 57 · Klickstrecke nur hinter REACT_APP_BODENKLICK_STRECKE=1 (aus = kein Knopf). */}
            {BODENKLICK_AN && (
              <Button
                data-testid="bodenklick-toggle"
                variant="outline"
                size="sm"
                onClick={() => setShowBodenklick((v) => !v)}
                className={`font-mono text-[11px] uppercase tracking-widest ${
                  showBodenklick ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-border"
                }`}
              >
                Klickstrecke Bodenklicks {showBodenklick ? "schließen" : "öffnen"}
              </Button>
            )}
            {!sequence?.calibration?.homography && (
              <span
                data-testid="anchor-collect-hint"
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
              >
                Ohne Basis-Kalibrierung: »Freies Zeichnen« (2–3 sichtbare Linien nachziehen) oder P3P nutzen
              </span>
            )}
          </div>
          {showAnchorMode && (
            <div className="mt-4">
              <AnchorCollectMode
                // Wechselt die Klickliste das Video, beginnt der Modus
                // frisch — sonst trüge er Zustand des vorigen Videos.
                key={sequenceId}
                sequenceId={sequenceId}
                sequence={sequence}
                onAnchorsChanged={refreshSeq}
                zielT={sprung.anker ? sprung.t : null}
                // 🆕 Auftrag LP1, R2 · `liste=lp1` geht an die
                // Linienprüfung, jede andere Liste wie bisher an die Klickliste.
                klickliste={ebenen.klickliste}
                linienpruefung={ebenen.linienpruefung}
              />
            </div>
          )}
          {/* 🆕 Auftrag 58, R4 · Anker-Prüfliste unter dem Sammelmodus, nur hinter dem Schalter der Klickstrecke;
              lädt neu, sobald refreshSeq nach einer Anker-Änderung die Sequenz neu setzt. */}
          {BODENKLICK_AN && showAnchorMode && (
            <div className="mt-3">
              <AnkerPruefliste key={`ap-${sequenceId}`} sequenceId={sequenceId} stand={sequence} />
            </div>
          )}
          {BODENKLICK_AN && showBodenklick && (
            <div className="mt-4">
              <BodenklickStrecke key={`bk-${sequenceId}`} sequenceId={sequenceId} />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 px-8 py-6">
        {step === 1 && <Step1Upload onComplete={onStep1Done} />}
        {step === 2 && sequenceId && (
          <Step2Calibration sequenceId={sequenceId} sequence={sequence} onComplete={onStep2Done} />
        )}
        {step === 3 && sequenceId && (
          <SequenceLab sequenceId={sequenceId} sequence={sequence} onFinish={onFinish} />
        )}
      </div>

      <footer className="px-8 py-5 border-t border-border bg-card/40 flex items-center justify-between">
        <Button
          data-testid={TID.wizardBackBtn}
          variant="ghost"
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          className="hover:bg-accent hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Step {step} / 3
        </div>
        <Button
          data-testid={TID.wizardNextBtn}
          variant="ghost"
          disabled={step === 3 || !sequenceId}
          onClick={() => setStep((s) => Math.min(3, s + 1))}
          className="hover:bg-accent hover:text-primary"
        >
          Next <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </footer>
    </div>
  );
}
